import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Check, Star, ArrowRight, HelpCircle, ChevronDown, Music, Sparkles, Shield, X, ChevronLeft, ChevronRight, Mail, Send, Phone } from 'lucide-react'
import ContactForm from '../components/ContactForm'
import ReviewCard from '../components/ReviewCard'
import SEO from '../components/SEO'

export default function Home() {
  const [services, setServices] = useState([])
  const [reviews, setReviews] = useState([])
  const [faqs, setFaqs] = useState([])
  const [openFaq, setOpenFaq] = useState(null)
  const [activeImageIndex, setActiveImageIndex] = useState(null)
  const [activeServiceIndex, setActiveServiceIndex] = useState(0)
  const [currentHeroImageIndex, setCurrentHeroImageIndex] = useState(0)
  const [heroVideoReady, setHeroVideoReady] = useState(false)

  const reviewsScrollRef = useRef(null)
  const servicesScrollRef = useRef(null)

  const scrollReviews = (direction) => {
    if (reviewsScrollRef.current) {
      const { scrollLeft, clientWidth } = reviewsScrollRef.current
      const cardWidth = reviewsScrollRef.current.firstChild?.clientWidth || clientWidth
      const scrollTo = direction === 'left'
        ? scrollLeft - cardWidth - 32
        : scrollLeft + cardWidth + 32
      reviewsScrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' })
    }
  }

  const scrollServices = (direction) => {
    if (servicesScrollRef.current) {
      const { scrollLeft, clientWidth } = servicesScrollRef.current
      const cardWidth = servicesScrollRef.current.firstChild?.clientWidth || clientWidth
      const scrollTo = direction === 'left'
        ? scrollLeft - cardWidth - 16
        : scrollLeft + cardWidth + 16
      servicesScrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' })
    }
  }

  const serviceOrder = ['bryllup', 'firmafest', 'foedselsdag', 'sax', 'vinyl']

  const getOverlayClass = (slug) => {
    const s = slug?.toLowerCase() || ''
    if (s.includes('bryllup')) {
      return 'bg-amber-700/50 mix-blend-multiply transition-all duration-500 group-hover:bg-[#bb9b56] group-hover:mix-blend-normal group-hover:opacity-95'
    }
    if (s.includes('firmafest')) {
      return 'bg-violet-800/50 mix-blend-multiply transition-all duration-500 group-hover:bg-[#bb9b56] group-hover:mix-blend-normal group-hover:opacity-95'
    }
    if (s.includes('foedselsdag') || s.includes('fødselsdag')) {
      return 'bg-teal-600/50 mix-blend-multiply transition-all duration-500 group-hover:bg-[#bb9b56] group-hover:mix-blend-normal group-hover:opacity-95'
    }
    if (s.includes('sax')) {
      return 'bg-amber-900/50 mix-blend-multiply transition-all duration-500 group-hover:bg-[#bb9b56] group-hover:mix-blend-normal group-hover:opacity-95'
    }
    if (s.includes('vinyl')) {
      return 'bg-zinc-900/65 mix-blend-multiply transition-all duration-500 group-hover:bg-[#bb9b56] group-hover:mix-blend-normal group-hover:opacity-95'
    }
    return 'bg-zinc-900/65 mix-blend-multiply transition-all duration-500 group-hover:bg-[#bb9b56] group-hover:mix-blend-normal group-hover:opacity-95'
  }

  const galleryImages = [
    {
      url: "https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/6a6f183f-a8d2-4ab1-ba69-46555a150cfd.jpg",
      thumbUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/6a6f183f-a8d2-4ab1-ba69-46555a150cfd.jpg",
      alt: "DJ Ron9 Live Performance - Fyldt dansegulv og fantastisk lysshow",
      title: "Live Performance"
    },
    {
      url: "https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/cae1706e-9e2d-4a22-8efb-179f53434740.jpg",
      thumbUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/cae1706e-9e2d-4a22-8efb-179f53434740.jpg",
      alt: "DJ Ron9 ved pulten - Fokus på detaljen og den perfekte overgang",
      title: "DJ Ron9 ved pulten"
    },
    {
      url: "https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/img-20220326-211012-1.jpg",
      thumbUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/img-20220326-211012-1.jpg",
      alt: "Eksklusiv firmafest - Professionelt setup og glade gæster",
      title: "Eksklusiv Firmafest"
    },
    {
      url: "https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/img20231210021214.jpg",
      thumbUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/img20231210021214.jpg",
      alt: "Romantisk bryllup - Stemningsfuld belysning og uforglemmelig brudevals",
      title: "Stilfuldt Bryllup"
    },
    {
      url: "https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/img20251108224515.jpg",
      thumbUrl: "https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/img20251108224515.jpg",
      alt: "Rund fødselsdag - Høj energi, konfetti og de største klassikere",
      title: "Fødselsdagsfest"
    }
  ]

  // FAQ schema builder for rich snippets
  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "EntertainmentBusiness",
        "@id": "https://www.djron9.dk/#business",
        "name": "DJ Ron9",
        "url": "https://www.djron9.dk/",
        "logo": "https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/86054219-a7e4-4879-b662-316ddd7c6cb7.png",
        "image": "https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/6a6f183f-a8d2-4ab1-ba69-46555a150cfd.jpg",
        "description": "Professionel DJ med over 25 års erfaring til bryllup, firmafest, fødselsdag og events i København og på Sjælland. Eksklusivt lyd- og lysudstyr samt dansegaranti.",
        "email": "ronni@djron9.dk",
        "telephone": "+4550998282",
        "priceRange": "$$",
        "vatID": "DK34924139",
        "address": {
          "@type": "PostalAddress",
          "postalCode": "2100",
          "addressLocality": "København Ø",
          "addressCountry": "DK"
        },
        "areaServed": [
          { "@type": "City", "name": "København" },
          { "@type": "AdministrativeArea", "name": "Sjælland" }
        ]
      },
      {
        "@type": "WebSite",
        "@id": "https://www.djron9.dk/#website",
        "url": "https://www.djron9.dk/",
        "name": "DJ Ron9",
        "publisher": {
          "@id": "https://www.djron9.dk/#business"
        }
      },
      ...(faqs && faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "mainEntity": faqs.map((faq) => ({
                "@type": "Question",
                "name": faq.question?.da || faq.question || "",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": (faq.answer?.da || faq.answer || "").replace(/<[^>]*>?/gm, "").trim()
                }
              }))
            }
          ]
        : [])
    ]
  }

  useEffect(() => {
    let timer = null
    const activateVideo = () => {
      setHeroVideoReady(true)
      cleanup()
    }
    const cleanup = () => {
      if (timer) clearTimeout(timer)
      window.removeEventListener('scroll', activateVideo)
      window.removeEventListener('touchstart', activateVideo)
      window.removeEventListener('click', activateVideo)
    }
    window.addEventListener('scroll', activateVideo, { passive: true, once: true })
    window.addEventListener('touchstart', activateVideo, { passive: true, once: true })
    window.addEventListener('click', activateVideo, { passive: true, once: true })
    timer = setTimeout(activateVideo, 1200)

    return cleanup
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImageIndex((prev) => (prev + 1) % galleryImages.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [galleryImages.length])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeImageIndex === null) return
      if (e.key === 'Escape') setActiveImageIndex(null)
      if (e.key === 'ArrowRight') {
        setActiveImageIndex((prev) => (prev + 1) % galleryImages.length)
      }
      if (e.key === 'ArrowLeft') {
        setActiveImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeImageIndex])

  useEffect(() => {
    // Hent ydelser
    fetch('/_api/public/content/services')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data.filter((s) => s.published !== false))
        }
      })
      .catch((err) => console.error('Fejl ved hentning af ydelser:', err))

    // Hent anmeldelser
    fetch('/_api/public/content/reviews')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setReviews(data) // Hent alle anmeldelser til slideren
      })
      .catch((err) => console.error('Fejl ved hentning af anmeldelser:', err))

    // Hent FAQs
    fetch('/_api/public/content/faqs')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setFaqs(data)
      })
      .catch((err) => console.error('Fejl ved hentning af FAQs:', err))
  }, [])

  useEffect(() => {
    if (window.location.hash === '#ydelser') {
      const timer = setTimeout(() => {
        document.getElementById('ydelser')?.scrollIntoView({ behavior: 'smooth' })
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [services])

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const sortedServices = [...services].sort((a, b) => {
    const slugA = a.service_slug?.da || a.slug?.da || ''
    const slugB = b.service_slug?.da || b.slug?.da || ''
    const indexA = serviceOrder.findIndex((o) => slugA.toLowerCase().includes(o))
    const indexB = serviceOrder.findIndex((o) => slugB.toLowerCase().includes(o))
    return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB)
  })

  return (
    <div className="bg-black text-white">
      <SEO
        title="DJ Ron9 | Professionel DJ til bryllup, firmafest & fødselsdag"
        description="Professionel DJ med over 25 års erfaring til bryllup, firmafest, fødselsdag og events i København og på Sjælland. Eksklusivt lyd- og lysudstyr samt dansegaranti."
        canonical="/"
        ogImage="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/6a6f183f-a8d2-4ab1-ba69-46555a150cfd.jpg"
        schema={homeSchema}
      />
      {/* Hero Section */}
      <section className="relative flex min-h-[65vh] sm:min-h-[80vh] items-center justify-center overflow-hidden pt-16 pb-24 sm:pt-20 sm:pb-28">
        {/* Background Video with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 h-full w-full object-cover opacity-100"
          >
            <source src="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-header-forside.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="font-serif text-3xl font-bold tracking-tight sm:text-5xl md:text-6xl text-white leading-tight">
            Professionel DJ med over 25 års erfaring til bryllup, firmafest, fødselsdag & andre events
          </h1>

          <p className="mx-auto mt-6 max-w-3xl text-sm text-zinc-200 sm:text-lg md:text-xl leading-relaxed font-normal">
            DJ Ron9 leverer den perfekte musikalske ramme med professionelt lyd- og lys udstyr tilpasset jeres fest i København og på Sjælland – uanset om det er dit livs bryllupsfest, firmaevent, DJ & saxofon eller fødselsdagsfest holder jeg dansegulvet fyldt hele natten
          </p>

          {/* Hero CTAs */}
          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#kontakt"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#bb9b56] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#bb9b56]/20 transition-all hover:scale-105 hover:bg-[#a88645] text-center cursor-pointer"
            >
              Få et uforpligtende tilbud
            </a>
          </div>
        </div>

        {/* Decorative bottom fade */}
        <div className="absolute -bottom-1 left-0 right-0 h-24 bg-gradient-to-t from-black to-transparent pointer-events-none" />

        {/* Featured Single Review Inside Bottom of Hero Section */}
        {reviews.length > 0 && (() => {
          const subpageNames = [
            'ria',
            'karppinen',
            'camilla vanesa',
            'zeltner',
            'anne vedel',
            'bentzen',
            'christina & philip',
            'line brinch',
          ]
          const featuredReview =
            reviews.find((r) => (r.name || '').toLowerCase().includes('carina sigurdsson')) ||
            reviews.find((r) => !subpageNames.some((n) => (r.name || '').toLowerCase().includes(n))) ||
            reviews[0]
          const rating = featuredReview.rating || 5
          return (
            <div className="absolute bottom-3 sm:bottom-6 left-0 right-0 z-20">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="relative overflow-hidden rounded-xl border border-amber-500/30 shadow-backlight-hover bg-zinc-900/80 md:shadow-backlight md:border-zinc-800/80 md:bg-zinc-900/60 md:hover:shadow-backlight-hover md:hover:border-amber-500/30 px-4 py-3 sm:px-6 sm:py-3.5 backdrop-blur-md transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 md:gap-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
                      {/* Stars & Author */}
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center gap-0.5 text-[#bb9b56]">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3.5 w-3.5 ${i < rating ? 'fill-[#bb9b56] text-[#bb9b56]' : 'text-zinc-700'}`}
                            />
                          ))}
                        </div>
                        <span className="text-xs font-bold text-white whitespace-nowrap">
                          {featuredReview.name}
                        </span>
                      </div>

                      {/* Review text */}
                      <p className="text-xs sm:text-sm text-zinc-300 italic truncate leading-normal">
                        "{featuredReview.text?.da || featuredReview.text}"
                      </p>
                    </div>

                    {/* Link to all reviews */}
                    <div className="shrink-0 flex items-center justify-end">
                      <Link
                        to="/anmeldelser"
                        className="text-xs font-semibold text-[#bb9b56] hover:text-amber-400 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                      >
                        Læs anmeldelser
                        <span aria-hidden="true">&rarr;</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
      </section>

      {/* Intro Section Under Hero */}
      <section className="py-16 sm:py-20 bg-black border-b border-zinc-900">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center sm:text-left">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
            Når du booker DJ Ron9 får du over 25 års erfaring fra de danske dansegulve
          </h2>
          <div className="mt-6 space-y-4 text-zinc-300 text-base sm:text-lg leading-relaxed">
            <p>
              Som passioneret mobil DJ i København og på Sjælland læser jeg rummet og rammer den helt rigtige stemning – uanset om det er firmafester, bryllupper, fødselsdage eller andre festlige events.
            </p>
            <p className="font-semibold text-white pt-2">
              Hvad betyder det for din fest:
            </p>
            <ul className="space-y-2.5 pl-5 list-disc marker:text-[#bb9b56]">
              <li>
                <strong className="text-white font-semibold">Evnen til at læse rummet:</strong> Rutine i at samle gæsterne og holde dansegulvet i gang hele natten.
              </li>
              <li>
                <strong className="text-white font-semibold">Kompromisløs kvalitet:</strong> Velspillende lydanlæg, professionelt lysshow og en skræddersyet musikprofil.
              </li>
              <li>
                <strong className="text-white font-semibold">Tryghed &amp; forberedelse:</strong> Grundig planlægning og en musikalsk rød tråd tilpasset jeres ønsker.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="ydelser" className="py-20 bg-zinc-950/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
              Professionelle DJ-løsninger
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-zinc-400 text-sm sm:text-base">
              Uanset anledningen tilpasser jeg musikken, så den passer perfekt til jeres ønsker og gæster.
            </p>
          </div>

          {/* 4 Service Boxes Grid (All visible at once on desktop, responsive on smaller screens) */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Box 1: DJ til bryllup */}
            <Link
              to="/dj-til-bryllup"
              className="group relative flex flex-col items-center justify-center h-[320px] sm:h-[360px] lg:h-[380px] overflow-hidden rounded-xl border border-amber-500/30 shadow-backlight-hover bg-zinc-950 md:shadow-backlight md:border-zinc-800/80 md:hover:shadow-backlight-hover md:hover:border-amber-500/30 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none" />
              <img
                src="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-bryllup.webp"
                alt="DJ til bryllup"
                loading="lazy"
                decoding="async"
                width="400"
                height="380"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20 group-hover:from-black/95 transition-all" />
              <div className="relative z-10 px-6 text-center">
                <h3 className="font-sans text-xl sm:text-2xl font-extrabold uppercase tracking-widest text-white transition-transform duration-300 group-hover:scale-105">
                  DJ til bryllup
                </h3>
              </div>
            </Link>

            {/* Box 2: DJ til firmafest */}
            <Link
              to="/lej-dj-til-firmafest"
              className="group relative flex flex-col items-center justify-center h-[320px] sm:h-[360px] lg:h-[380px] overflow-hidden rounded-xl border border-amber-500/30 shadow-backlight-hover bg-zinc-950 md:shadow-backlight md:border-zinc-800/80 md:hover:shadow-backlight-hover md:hover:border-amber-500/30 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none" />
              <img
                src="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-firmafest.webp"
                alt="DJ til firmafest"
                loading="lazy"
                decoding="async"
                width="400"
                height="380"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20 group-hover:from-black/95 transition-all" />
              <div className="relative z-10 px-6 text-center">
                <h3 className="font-sans text-xl sm:text-2xl font-extrabold uppercase tracking-widest text-white transition-transform duration-300 group-hover:scale-105">
                  DJ til firmafest
                </h3>
              </div>
            </Link>

            {/* Box 3: DJ til fødselsdag */}
            <Link
              to="/dj-til-foedselsdag"
              className="group relative flex flex-col items-center justify-center h-[320px] sm:h-[360px] lg:h-[380px] overflow-hidden rounded-xl border border-amber-500/30 shadow-backlight-hover bg-zinc-950 md:shadow-backlight md:border-zinc-800/80 md:hover:shadow-backlight-hover md:hover:border-amber-500/30 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none" />
              <img
                src="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-foedselsdag.webp"
                alt="DJ til fødselsdag"
                loading="lazy"
                decoding="async"
                width="400"
                height="380"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20 group-hover:from-black/95 transition-all" />
              <div className="relative z-10 px-6 text-center">
                <h3 className="font-sans text-xl sm:text-2xl font-extrabold uppercase tracking-widest text-white transition-transform duration-300 group-hover:scale-105">
                  DJ til fødselsdag
                </h3>
              </div>
            </Link>

            {/* Box 4: DJ & saxofon */}
            <Link
              to="/dj-og-saxofon"
              className="group relative flex flex-col items-center justify-center h-[320px] sm:h-[360px] lg:h-[380px] overflow-hidden rounded-xl border border-amber-500/30 shadow-backlight-hover bg-zinc-950 md:shadow-backlight md:border-zinc-800/80 md:hover:shadow-backlight-hover md:hover:border-amber-500/30 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none" />
              <img
                src="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/djron9-dj-saxofon.webp"
                alt="DJ & saxofon"
                loading="lazy"
                decoding="async"
                width="400"
                height="380"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20 group-hover:from-black/95 transition-all" />
              <div className="relative z-10 px-6 text-center">
                <h3 className="font-sans text-xl sm:text-2xl font-extrabold uppercase tracking-widest text-white transition-transform duration-300 group-hover:scale-105">
                  DJ & saxofon
                </h3>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Social Proof / Reviews Section */}
      <section className="py-20 border-t border-zinc-900 bg-zinc-950/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
                Hvad siger kunderne?
              </h2>
              <p className="mt-3 text-zinc-400">
                Læs anmeldelser fra par, virksomheder og fødselarer, der har holdt fest med DJ Ron9.
              </p>
            </div>
            <Link
              to="/anmeldelser"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#bb9b56] hover:text-[#a88645] md:mt-0"
            >
              Se alle anmeldelser
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="relative mt-12 px-4 sm:px-0">
            {/* Left Arrow */}
            <button
              onClick={() => scrollReviews('left')}
              className="absolute lg:top-[-4.5rem] lg:right-16 lg:left-auto lg:translate-y-0 xl:top-1/2 xl:-translate-y-1/2 xl:left-[-4rem] xl:right-auto z-10 p-3 rounded-full bg-zinc-950/80 border border-zinc-800 text-white/80 hover:text-amber-400 hover:border-amber-500/30 hover:bg-zinc-900 transition-all duration-200 shadow-xl backdrop-blur-sm cursor-pointer"
              aria-label="Forrige anmeldelser"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Right Arrow */}
            <button
              onClick={() => scrollReviews('right')}
              className="absolute lg:top-[-4.5rem] lg:right-4 lg:translate-y-0 xl:top-1/2 xl:-translate-y-1/2 xl:right-[-4rem] z-10 p-3 rounded-full bg-zinc-950/80 border border-zinc-800 text-white/80 hover:text-amber-400 hover:border-amber-500/30 hover:bg-zinc-900 transition-all duration-200 shadow-xl backdrop-blur-sm cursor-pointer"
              aria-label="Næste anmeldelser"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* Scroll Container */}
            <div
              ref={reviewsScrollRef}
              className="flex gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] py-6 px-6 scroll-px-6"
            >
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="snap-start shrink-0 w-full sm:w-[calc(50%-16px)] lg:w-[calc(33.333%-21.333px)]"
                >
                  <ReviewCard review={review} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section (Fordele) */}
      <section className="py-20 border-t border-zinc-900 bg-black">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
                Din garanti for en uforglemmelig dansefest
              </h2>
              <p className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base lg:text-sm xl:text-base">
                Når du booker DJ Ron9, overlader du intet til tilfældighederne. Jeg har over 25 års erfaring med at skabe den perfekte feststemning og medbringer kun det bedste udstyr på markedet, flot lysshow og en up-to-date musiksamling.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 text-amber-400 mt-0.5">
                    <Check className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">Professionelt udstyr</h3>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 text-amber-400 mt-0.5">
                    <Check className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">Personlig planlægning</h3>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 text-amber-400 mt-0.5">
                    <Check className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">Bredt musikkatalog</h3>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 text-amber-400 mt-0.5">
                    <Check className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">Erfaren DJ & Dansegaranti</h3>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative order-first lg:order-last">
              <div className="relative overflow-hidden border border-zinc-800 bg-zinc-950 shadow-backlight-hover md:shadow-backlight md:hover:shadow-backlight-hover transition-all duration-300">
                <video
                  src="/Video/DJ-header-video_2.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                  preload="none"
                  className="object-cover w-full h-[450px] object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section (Asymmetric Collage) */}
      <section className="py-20 border-t border-zinc-900 bg-zinc-950/40 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Asymmetric Image Collage Grid */}
            <div className="lg:col-span-6 grid grid-cols-6 gap-3 sm:gap-4 lg:order-first">
              {/* Image 1 (Feature) */}
              <div 
                onClick={() => setActiveImageIndex(0)}
                className="col-span-4 group relative h-48 sm:h-56 md:h-60 overflow-hidden rounded-xl border border-[#bb9b56]/40 bg-zinc-950 shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_45px_rgba(168,85,247,0.65)] hover:border-[#bb9b56] transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none z-10" />
                <img 
                  src={galleryImages[0].thumbUrl || galleryImages[0].url} 
                  alt={galleryImages[0].alt} 
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="240"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20 group-hover:from-black/60 transition-all pointer-events-none" />
              </div>

              {/* Image 2 */}
              <div 
                onClick={() => setActiveImageIndex(1)}
                className="col-span-2 group relative h-48 sm:h-56 md:h-60 overflow-hidden rounded-xl border border-[#bb9b56]/40 bg-zinc-950 shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_45px_rgba(168,85,247,0.65)] hover:border-[#bb9b56] transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none z-10" />
                <img 
                  src={galleryImages[1].thumbUrl || galleryImages[1].url} 
                  alt={galleryImages[1].alt} 
                  loading="lazy"
                  decoding="async"
                  width="200"
                  height="240"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20 group-hover:from-black/60 transition-all pointer-events-none" />
              </div>

              {/* Image 3 */}
              <div 
                onClick={() => setActiveImageIndex(2)}
                className="col-span-2 group relative h-32 sm:h-36 md:h-40 overflow-hidden rounded-xl border border-[#bb9b56]/40 bg-zinc-950 shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_45px_rgba(168,85,247,0.65)] hover:border-[#bb9b56] transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none z-10" />
                <img 
                  src={galleryImages[2].thumbUrl || galleryImages[2].url} 
                  alt={galleryImages[2].alt} 
                  loading="lazy"
                  decoding="async"
                  width="200"
                  height="240"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20 group-hover:from-black/60 transition-all pointer-events-none" />
              </div>

              {/* Image 4 */}
              <div 
                onClick={() => setActiveImageIndex(3)}
                className="col-span-2 group relative h-32 sm:h-36 md:h-40 overflow-hidden rounded-xl border border-[#bb9b56]/40 bg-zinc-950 shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_45px_rgba(168,85,247,0.65)] hover:border-[#bb9b56] transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none z-10" />
                <img 
                  src={galleryImages[3].thumbUrl || galleryImages[3].url} 
                  alt={galleryImages[3].alt} 
                  loading="lazy"
                  decoding="async"
                  width="200"
                  height="240"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20 group-hover:from-black/60 transition-all pointer-events-none" />
              </div>

              {/* Image 5 */}
              <div 
                onClick={() => setActiveImageIndex(4)}
                className="col-span-2 group relative h-32 sm:h-36 md:h-40 overflow-hidden rounded-xl border border-[#bb9b56]/40 bg-zinc-950 shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_45px_rgba(168,85,247,0.65)] hover:border-[#bb9b56] transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="absolute inset-0 rounded-xl bg-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl pointer-events-none z-10" />
                <img 
                  src={galleryImages[4].thumbUrl || galleryImages[4].url} 
                  alt={galleryImages[4].alt} 
                  loading="lazy"
                  decoding="async"
                  width="200"
                  height="240"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20 group-hover:from-black/60 transition-all pointer-events-none" />
              </div>
            </div>

            {/* Right Column: Content Info */}
            <div className="lg:col-span-6 space-y-6">
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                Oplev Stemningen Live
              </h2>
              
              <p className="text-sm md:text-base lg:text-sm xl:text-base text-zinc-300 leading-relaxed font-light">
                Billeder lyver ikke. Her kan du se et udpluk af den fantastiske energi, de fyldte dansegulve og det eksklusive setup, som DJ Ron9 bringer til live ved bryllupper, firmafester og private events. Hvert arrangement skræddersys med knivskarp præcision og luksuriøs club-stemning.
              </p>

              <div className="pt-4">
                <a
                  href="#kontakt"
                  onClick={(e) => {
                    e.preventDefault()
                    document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })
                  }}
                  className="inline-flex items-center gap-3 bg-[#bb9b56] hover:bg-[#a88645] text-white font-semibold text-sm uppercase tracking-wider px-6 py-3.5 rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-[#bb9b56]/15 cursor-pointer"
                >
                  Få et tilbud til din fest
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 p-4 backdrop-blur-sm animate-fade-in">
          {/* Close Button */}
          <button 
            onClick={() => setActiveImageIndex(null)}
            className="absolute top-6 right-6 text-white/70 hover:text-amber-400 transition-colors p-2 hover:bg-zinc-900/50 rounded-full"
            aria-label="Luk galleri"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Navigation Arrows */}
          <button 
            onClick={() => setActiveImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-amber-400 transition-colors p-3 bg-zinc-900/50 hover:bg-zinc-900 rounded-full border border-zinc-800"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button 
            onClick={() => setActiveImageIndex((prev) => (prev + 1) % galleryImages.length)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-amber-400 transition-colors p-3 bg-zinc-900/50 hover:bg-zinc-900 rounded-full border border-zinc-800"
            aria-label="Næste billede"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Image Container */}
          <div className="max-w-5xl max-h-[75vh] flex flex-col items-center justify-center relative">
            <img 
              src={galleryImages[activeImageIndex].url} 
              alt={galleryImages[activeImageIndex].alt} 
              className="max-w-full max-h-[75vh] object-contain rounded-lg border border-zinc-800 shadow-2xl"
            />
          </div>

          {/* Caption */}
          <div className="mt-6 text-center max-w-xl px-4">
            <div className="text-xs text-zinc-500">
              Billede {activeImageIndex + 1} af {galleryImages.length}
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <section className="py-20 border-t border-zinc-900 bg-black">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
              Ofte stillede spørgsmål
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-zinc-400">
              Her finder du svar på de mest almindelige spørgsmål om booking, udstyr og afvikling.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                className="rounded-xl border border-zinc-800 bg-zinc-950/50 overflow-hidden shadow-backlight-hover md:shadow-backlight md:hover:shadow-backlight-hover transition-all duration-300"
              >
                <button
                  type="button"
                  onClick={() => toggleFaq(index)}
                  aria-expanded={openFaq === index}
                  aria-controls={`faq-answer-${faq.id || index}`}
                  id={`faq-question-${faq.id || index}`}
                  className="flex w-full items-center justify-between px-6 py-5 text-left font-medium text-white hover:text-amber-400 focus:outline-none"
                >
                  <span>{faq.question?.da}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-zinc-500 transition-transform duration-200 ${
                      openFaq === index ? 'rotate-180 text-amber-400' : ''
                    }`}
                  />
                </button>

                <div
                  id={`faq-answer-${faq.id || index}`}
                  role="region"
                  aria-labelledby={`faq-question-${faq.id || index}`}
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    openFaq === index ? 'max-h-[500px] border-t border-zinc-900' : 'max-h-0'
                  }`}
                >
                  <div
                    className="px-6 py-5 text-sm text-zinc-400 leading-relaxed prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: faq.answer?.da }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="relative py-20 border-t border-zinc-900 bg-zinc-950">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            {/* Contact Info */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
                  Lad os planlægge <br />
                  <span className="text-white">
                    din næste fest
                  </span>
                </h2>
                <p className="mt-4 text-zinc-400 leading-relaxed">
                  Udfyld formularen til højre, eller kontakt mig direkte via telefon eller e-mail. Jeg vender tilbage med et uforpligtende tilbud inden for 12 timer.
                </p>
              </div>

              <div className="space-y-6 border-t border-zinc-900 pt-8">
                <div className="flex items-center gap-4">
                  <Phone className="h-6 w-6 text-white shrink-0" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Telefon</p>
                    <span className="text-lg font-semibold text-white">
                      50 99 82 82
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <Mail className="h-6 w-6 text-white shrink-0" />
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Skriv en e-mail</p>
                    <a href="mailto:ronni@djron9.dk" className="text-lg font-semibold text-white hover:text-amber-400 transition-colors break-all">
                      ronni@djron9.dk
                    </a>
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-900 pt-8 text-xs text-zinc-500 space-y-2">
                <p className="font-semibold text-zinc-400">DJ Ron9</p>
                <p>2100 København Ø</p>
                <p>CVR: 34924139</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
